import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '../lib/auth';
import { usePendingInbox } from '../lib/useEnrollment';
import { useViewingAs } from '../lib/useViewingAs';
import { Avatar } from './Avatar';

type Tab = {
  id: string;
  label: string;
  to: string;
  icon: ReactNode;
  matches: (path: string) => boolean;
};

const TABS: Tab[] = [
  {
    id: 'home',
    label: 'Camp',
    to: '/',
    matches: (p) => p === '/',
    icon: <path d="M3 21 L12 4 L21 21 Z M9 14 H15 V21 H9 Z" />,
  },
  {
    id: 'patches',
    label: 'Patches',
    to: '/patches',
    matches: (p) => p === '/patches' || p.startsWith('/patches/'),
    icon: (
      <g>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
      </g>
    ),
  },
  {
    id: 'sash',
    label: 'Sashes',
    to: '/sash',
    matches: (p) => p === '/sash',
    icon: <path d="M7 3 L17 3 L14 21 L10 21 Z" />,
  },
  {
    id: 'family',
    label: 'Family',
    to: '/family',
    matches: (p) => p === '/family',
    icon: (
      <g>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="10" r="2.5" />
        <path d="M3 20 C3 16 6 14 9 14 C12 14 15 16 15 20" />
        <path d="M14 20 C14 17 16 16 18 16 C20 16 21 17 21 20" />
      </g>
    ),
  },
  {
    id: 'inbox',
    label: 'Inbox',
    to: '/inbox',
    matches: (p) => p === '/inbox',
    icon: (
      <g>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </g>
    ),
  },
];

export function MobileBottomNav() {
  const location = useLocation();
  const { profile } = useSession();
  const { data: inbox } = usePendingInbox();
  const inboxCount = inbox?.length ?? 0;
  const { withAs } = useViewingAs();

  const isProfileActive = location.pathname === '/profile';

  return (
    <nav
      aria-label="Primary"
      style={{
        flexShrink: 0,
        background: 'var(--ink)',
        borderTop: '1.5px solid var(--ink)',
        paddingBottom: 'max(6px, env(safe-area-inset-bottom))',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-around',
      }}
    >
      {TABS.map((t) => {
        const active = t.matches(location.pathname);
        const showBadge = t.id === 'inbox' && inboxCount > 0;
        return (
          <Link
            key={t.id}
            to={withAs(t.to)}
            aria-label={t.label}
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              padding: '8px 4px 6px',
              textDecoration: 'none',
              color: active ? 'var(--gold, #D4A846)' : 'rgba(241,231,208,0.7)',
              position: 'relative',
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {t.icon}
            </svg>
            <span
              style={{
                fontFamily: 'JetBrains Mono',
                fontSize: 9,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              {t.label}
            </span>
            {showBadge && (
              <span
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 'calc(50% - 18px)',
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 8,
                  background: 'var(--brick)',
                  color: 'var(--cream)',
                  border: '1.5px solid var(--ink)',
                  fontFamily: 'Bevan',
                  fontSize: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {inboxCount}
              </span>
            )}
          </Link>
        );
      })}
      {/* Profile chip — sixth slot, slightly different look so the user can always find themself */}
      <Link
        to="/profile"
        aria-label="Profile"
        aria-current={isProfileActive ? 'page' : undefined}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          padding: '8px 4px 6px',
          textDecoration: 'none',
          color: isProfileActive ? 'var(--gold, #D4A846)' : 'rgba(241,231,208,0.7)',
        }}
      >
        {profile ? (
          <Avatar
            url={profile.avatar_url}
            name={profile.display_name}
            color={profile.member_color || '#1E3A2A'}
            size={22}
            fontSize={9}
            borderColor={isProfileActive ? 'var(--gold, #D4A846)' : 'rgba(241,231,208,0.7)'}
          />
        ) : (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
          </svg>
        )}
        <span
          style={{
            fontFamily: 'JetBrains Mono',
            fontSize: 9,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          You
        </span>
      </Link>
    </nav>
  );
}
